import React, { useContext, useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import { Input, Nav, NavItem, NavLink, Spinner } from "reactstrap";
import { useCookies } from "react-cookie";
import { Icon } from "../../../components/Component";
import { igGetAccounts, igGetChats } from "../../../services/igAdapter";
import { getDepartments } from "../../../services/departments";
import { IgChatContext } from "./IgChatContext";
import IgChatAsideBody from "./IgChatAsideBody";
import IgChatModalGreeting from "./IgChatModalGreeting";
import { Button } from "../../../components/Component";

const PAGE_SIZE = 20;

/**
 * IgChatAside — sidebar kiri halaman Instagram Platform.
 * Berisi dropdown pilih akun IG dan daftar conversation.
 *
 * Props:
 *   onSelectChat: function(chat) — dipanggil saat conversation dipilih
 */
const IgChatAside = ({ onSelectChat }) => {
  const { accountListState, chatListState, departmentMapState } = useContext(IgChatContext);
  const [cookies] = useCookies(["user", "departmentIds"]);
  const [, setDepartmentMap] = departmentMapState;

  const [, setAccountList] = accountListState;
  const [chatList, setChatList] = chatListState;

  // Ikuti pola WA module: cek roleFunctions untuk tentukan apakah perlu filter dept.
  const roleFunctions = JSON.parse(localStorage.getItem("permission") || "{}")?.chats?.access || [];
  const canSeeAllDept = roleFunctions.includes("Access_All_Dept");
  const onlyOwnDept = roleFunctions.includes("Access_Own_Dept") && !canSeeAllDept;
  const onlyOwn = roleFunctions.includes("Access_Own") && !canSeeAllDept && !onlyOwnDept; // SALES

  // Mapping akses:
  // Access_All_Dept  → lihat semua (AUDITOR, BDM, SUPER ADMIN, SYSTEM ADMIN)
  // Access_Own_Dept  → lihat chat dept sendiri (CS, ADMIN, HEAD)
  // Access_Own       → lihat chat yang di-assign ke diri sendiri (SALES)
  // Pass semua departmentIds user (bisa lebih dari satu dept)
  // SALES (onlyOwn) juga kirim departmentIds agar backend bisa scope travelista-international
  // hanya ke user yang memang di department tersebut.
  const departmentIds = (onlyOwnDept || onlyOwn)
    ? (Array.isArray(cookies.departmentIds)
        ? cookies.departmentIds.filter(Boolean).join(",")
        : cookies.departmentIds || "")
    : "";
  const departmentId = departmentIds; // alias untuk kompatibilitas kode di bawah

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Search state — debounced
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const debounceRef = useRef(null);

  // All slugs ref — kept in sync after accounts load
  const allSlugsRef = useRef([]);
  const scrollRef = useRef(null);
   // tambahan

  // Modal pengaturan greeting otomatis
  const [isModalGreetingOpen, setIsModalGreetingOpen] = useState(false);

  const onInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchTerm(value), 300);
  };

  // Fetch departments sekali saat mount untuk build lookup map id → {name, color}
  useEffect(() => {
    getDepartments(cookies.SIDikey, "", cookies.departmentIds, cookies.id, roleFunctions)
      .then((res) => {
        if (res?.data?.data) {
          const map = {};
          res.data.data.forEach((d) => { map[d.id] = { name: d.name, color: d.color }; });
          setDepartmentMap(map);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch chat saat komponen mount berdasarkan access level:
  // Access_Own_Dept  → by department_id (CS, ADMIN, HEAD)
  // Access_Own       → by assigned_user_id via backend, tidak perlu accounts (SALES)
  // Access_All_Dept  → fetch semua accounts lalu pass slugs (AUDITOR, BDM, SUPER, SYSTEM)
  useEffect(() => {
    if (departmentId) {
      fetchChats([], 0, true);
    } else if (onlyOwn) {
      // SALES: backend sudah filter by assigned_user_id via role header, tidak perlu accounts
      fetchChats([], 0, true);
    } else {
      const fetchAccounts = async () => {
        setIsLoadingAccounts(true);
        const res = await igGetAccounts();
        if (res?.data && Array.isArray(res.data)) {
          setAccountList(res.data);
          const slugs = res.data.map((a) => a.slug);
          allSlugsRef.current = slugs;
          if (slugs.length > 0) {
            fetchChats(slugs, 0, true);
          }
        }
        setIsLoadingAccounts(false);
      };
      fetchAccounts();
    }
    return () => clearTimeout(debounceRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChats = async (slugs, currentOffset, replace = false, search = "") => {
    setIsLoadingChats(true);
    const res = await igGetChats(slugs, PAGE_SIZE, currentOffset, search, departmentId);
    if (res?.data && Array.isArray(res.data)) {
      if (replace) {
        setChatList(res.data);
      } else {
        setChatList((prev) => [...prev, ...res.data]);
      }
      setHasMore(res.data.length === PAGE_SIZE);
    }
    setIsLoadingChats(false);
  };

  const handleLoadMore = () => {
    if (isLoadingChats || !hasMore) return;
    const nextOffset = offset + PAGE_SIZE;
    setOffset(nextOffset);
    const slugs = (departmentId || onlyOwn) ? [] : allSlugsRef.current;
    fetchChats(slugs, nextOffset);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 150) {
        handleLoadMore();
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isLoadingChats, hasMore, offset, departmentId, onlyOwn]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterChatByTab = (chats, tab) => {
    switch (tab) {
      case "unopened":
        return chats.filter((chat) => chat.unread_count > 0);
      case "opened":
        return chats.filter((chat) => chat.unread_count === 0 && chat.assigned_user_id !== null);
      case "pending":
        return chats.filter((chat) => chat.assigned_user_id === null);
      case "all":
      default:
        return chats;
    }
  };

  const toggleTab = (tab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
  };

  const filteredChatList = filterChatByTab(chatList, activeTab);

  // Server-side search — trigger saat searchTerm berubah
  useEffect(() => {
    if (!departmentId && !onlyOwn && allSlugsRef.current.length === 0) return;
    setOffset(0);
    const slugs = (departmentId || onlyOwn) ? [] : allSlugsRef.current;
    fetchChats(slugs, 0, true, searchTerm.trim() || "");
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="nk-chat-aside">
      <div className="nk-chat-aside-head">
        <div className="nk-chat-aside-user d-flex align-items-center">
          <h6 className="title mb-0">Instagram DM</h6>
        </div>
        <span
          className="badge badge-warning ms-2"
          title="Fitur ini belum dipublikasikan secara resmi"
          style={{ fontSize: 10, verticalAlign: "middle" }}
        >
          Feature under testing mode
        </span>
        <div
          className="btn btn-round btn-icon btn-light ms-2"
          title="Pengaturan Greeting Otomatis"
          style={{ cursor: "pointer" }}
          onClick={() => setIsModalGreetingOpen(true)}
        >
          <Icon name="setting" />
        </div>
      </div>

      <IgChatModalGreeting isOpen={isModalGreetingOpen} onClose={() => setIsModalGreetingOpen(false)} />

      <SimpleBar className="w-100">
        <div className="d-flex w-100" style={{ height: "40px" }}>
          <Nav tabs style={{ border: "none", flexWrap: "nowrap" }}>
            <NavItem>
              <NavLink
                style={{ height: "40px", color: activeTab === "all" ? "#4461F2" : "", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => toggleTab("all")}
              >
                All
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ height: "40px", color: activeTab === "unopened" ? "#4461F2" : "", cursor: "pointer" }}
                onClick={() => toggleTab("unopened")}
              >
                Unopened
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ height: "40px", color: activeTab === "opened" ? "#4461F2" : "", cursor: "pointer" }}
                onClick={() => toggleTab("opened")}
              >
                Opened
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ height: "40px", color: activeTab === "pending" ? "#4461F2" : "", cursor: "pointer", marginRight: "10px" }}
                onClick={() => toggleTab("pending")}
              >
                Pending
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </SimpleBar>

      <div className="nk-chat-aside-search p-2" style={{ backgroundColor: "#e5e9f2", marginBottom: "15px" }}>
        {isLoadingAccounts ? (
          <Spinner size="sm" />
        ) : (
          <div className="form-group mb-0">
            <div className="form-control-wrap d-flex">
              <div className="form-icon form-icon-left">
                <Icon name="search" />
              </div>
              <Input
                type="text"
                className="form-round"
                id="ig-chat-search"
                placeholder="Search by name"
                value={searchInput}
                onChange={onInputChange}
                style={{ backgroundColor: "white" }}
              />

              {/* Filter Tags — coming soon */}
              <div
                className="btn btn-round btn-icon btn-light dropdown-toggle ml-2"
                title="Filter by tag (coming soon)"
                style={{ opacity: 0.45, cursor: "not-allowed" }}
              >
                <Icon name="filter-alt" />
              </div>

              {/* Filter Campaign — coming soon */}
              <div
                className="btn btn-round btn-icon btn-light dropdown-toggle"
                title="Filter by campaign (coming soon)"
                style={{ opacity: 0.45, cursor: "not-allowed" }}
              >
                <Icon name="cc-alt2" />
              </div>

              {/* Filter User — coming soon */}
              <div
                className="btn btn-round btn-icon btn-light dropdown-toggle"
                title="Filter by user (coming soon)"
                style={{ opacity: 0.45, cursor: "not-allowed" }}
              >
                <Icon name="user-list" />
              </div>
            </div>
          </div>
        )}
      </div>

      <SimpleBar className="nk-chat-aside-body" id="chat-list" scrollableNodeProps={{ ref: scrollRef }}>
        <div className="nk-chat-list">
          <h6 className="title overline-title-alt">
            {activeTab === "all" ? "Semua Conversation" : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </h6>

          <ul className="chat-list">
            {isLoadingChats && chatList.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center py-4">
                <Spinner color="primary" />
              </div>
            ) : filteredChatList.length > 0 ? (
              filteredChatList.map((chat) => (
                <IgChatAsideBody key={chat.conversation_id} item={chat} onSelect={onSelectChat} />
              ))
            ) : searchTerm.trim() ? (
              <p className="m-3 text-muted text-center" style={{ fontSize: 13 }}>
                Tidak ada conversation yang cocok
              </p>
            ) : (
              <p className="m-3 text-muted text-center" style={{ fontSize: 13 }}>
                {activeTab === "all"
                 ? "Tidak ada conversation"
                : `Tidak ada conversation dengan status "${activeTab}"`}
              </p>
            )}
          </ul>

          {isLoadingChats && chatList.length > 0 && (
            <div className="d-flex justify-content-center mt-2 mb-3">
                <Button outline color="light" size="sm">
                  {isLoadingChats ? (
                    <Spinner></Spinner>
                  ) : (
                    <div className="mb-1 mt-1">
                      <span>Load More Chats</span>
                      <Icon name="reload-alt" />
                    </div>
                  )}
                </Button>
              </div>
          )}
        </div>
      </SimpleBar>
    </div>
  );
};

export default IgChatAside;
